import SwitchButton from "@components/buttons/SwitchButton"
import GlobalDataContext from "@components/Layout/GlobalDataContext"
import { ItemContainer } from "@components/MenuItem"
import { MenuItemTogglableCompactProps } from "@components/MenuItemTogglableCompact"
import ProductFilter from "@components/ProductFilterDesktop"
import animation, { cubicBezier } from "@constants/animation"
import colors from "@constants/colors"
import useScrollHandler from "@hooks/useScrollHandler"
import { getIn } from "@utils/index"
import { FilterFunnelProps, MenuDataProps } from "@utils/types"
import React from "react"
import styled from "styled-components"

import BrandFilter from "./BrandFilter"
import Breadcrumbs from "./Breadcrumbs"
import DeviceFilter from "./DeviceFilter"
import DeviceTypeFilter from "./DeviceTypeFilter"

const Container = styled.div`
  width: calc(2 / 12 * 100vw);
  height: 100%;
  min-width: 300px;
`

const StickyWrapper = styled.div<{
  $promoBannerHeight: number
  $shouldShorten: boolean
}>`
  position: fixed;
  position: sticky;
  top: ${({ $promoBannerHeight }) => `calc(${$promoBannerHeight}px + 88px)`};
  width: calc(2 / 12 * 100vw);
  height: ${({ $promoBannerHeight }) =>
    `calc(100vh - ${$promoBannerHeight}px - 88px)`};
  min-width: 300px;
  padding-left: calc(0.5 / 12 * 100vw);
  padding-bottom: 56px;
  transition: transform ${animation.quick} ${cubicBezier};
  ${({ $shouldShorten }) =>
    $shouldShorten ? "transform: translateY(12px);" : ""}
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${colors.dividerLineGray};
`

const SpecialItem = styled(ItemContainer)`
  padding: 7.5px 0 7.5px 24px;
`

interface SideNavProps {
  activeBrands: number
  activeColors: number
  activeDiscountTiers: number
  activeProductSeries: number
  activeSubTypes: number
  brands: Array<any>
  colors: Array<any>
  discountTiers: Array<any>
  displayBanner: boolean
  filterFunnel: FilterFunnelProps
  filters: any
  globalData: any
  hasBundleProducts: boolean
  hasMagnetCompatibleProducts: boolean
  hasPrintedDesigns: boolean
  isClearance: boolean
  isCustomPrints: boolean
  menuData: MenuDataProps
  onResetFilters: (event) => void
  onToggleBundleProducts: () => void
  onToggleClearance: (event) => void
  onToggleInStock: (event) => void
  onToggleMagnetCompatibleProducts: () => void
  onTogglePrintedDesigns: () => void
  productSeries: Array<any>
  showBundleProducts: boolean
  showClearance: boolean
  showInStock: boolean
  showMagnetCompatibleProducts: boolean
  showPrintedDesigns: boolean
  subTypes: Array<MenuItemTogglableCompactProps>
}

const SideNav: React.FC<SideNavProps> = ({
  activeBrands,
  activeColors,
  activeDiscountTiers,
  activeProductSeries,
  activeSubTypes,
  brands,
  colors,
  discountTiers,
  displayBanner,
  filterFunnel,
  filters,
  globalData,
  hasBundleProducts,
  hasMagnetCompatibleProducts,
  hasPrintedDesigns,
  isClearance,
  isCustomPrints,
  menuData,
  onResetFilters,
  onToggleBundleProducts,
  onToggleClearance,
  onToggleInStock,
  onToggleMagnetCompatibleProducts,
  onTogglePrintedDesigns,
  productSeries,
  showBundleProducts,
  showClearance,
  showInStock,
  showMagnetCompatibleProducts,
  showPrintedDesigns,
  subTypes,
}) => {
  const { showPromoBanner }: any = React.useContext(GlobalDataContext)

  const [shortenHeader, setShortenHeader] = React.useState(false)
  const handleShortenHeight = () => {
    const html = document.getElementsByTagName("html")
    if (html && html[0] && html[0].getBoundingClientRect().top < -40) {
      setShortenHeader(true)
    } else {
      setShortenHeader(false)
    }
  }

  useScrollHandler({ onScroll: handleShortenHeight })

  const renderFilters = () => {
    const { brand, collection, device, deviceType, series } = filters

    if (
      isCustomPrints ||
      isClearance ||
      collection ||
      series ||
      (deviceType && brand && device)
    ) {
      return (
        <ProductFilter
          activeBrands={activeBrands}
          activeColors={activeColors}
          activeDiscountTiers={activeDiscountTiers}
          activeProductSeries={activeProductSeries}
          activeSubTypes={activeSubTypes}
          brands={brands}
          colors={colors}
          discountTiers={discountTiers}
          hasBundleProducts={hasBundleProducts}
          hasMagnetCompatibleProducts={hasMagnetCompatibleProducts}
          hasPrintedDesigns={hasPrintedDesigns}
          isClearance={isClearance}
          isCustomPrints={isCustomPrints}
          onResetFilters={onResetFilters}
          onToggleBundleProducts={onToggleBundleProducts}
          onToggleClearance={onToggleClearance}
          onToggleMagnetCompatibleProducts={onToggleMagnetCompatibleProducts}
          onTogglePrintedDesigns={onTogglePrintedDesigns}
          productSeries={!series ? productSeries : []}
          showBundleProducts={showBundleProducts}
          showClearance={showClearance}
          showMagnetCompatibleProducts={showMagnetCompatibleProducts}
          showPrintedDesigns={showPrintedDesigns}
          subTypes={subTypes}
        />
      )
    } else if (deviceType && brand) {
      return (
        <DeviceFilter
          brandSlug={brand.slug}
          data={menuData || {}}
          deviceTypeSlug={deviceType.slug}
        />
      )
    } else if (deviceType) {
      return (
        <BrandFilter
          data={menuData || {}}
          deviceTypeSlug={deviceType.slug}
          filterFunnel={filterFunnel}
        />
      )
    } else {
      return (
        <DeviceTypeFilter
          data={getIn(globalData, "menuItems", [])}
          filterFunnel={filterFunnel}
        />
      )
    }
  }

  const previousBreadcrumb = isClearance ? null : filters?.previous
  const currentBreadcrumb = isClearance
    ? filters.deviceType
      ? `Shop ${filters.deviceType.name} Sale`
      : "Shop Sale"
    : filters?.current

  return (
    <Container>
      <StickyWrapper
        $promoBannerHeight={showPromoBanner ? 36 : 0}
        $shouldShorten={shortenHeader}
      >
        <Breadcrumbs
          current={currentBreadcrumb}
          displayBanner={displayBanner}
          previous={previousBreadcrumb}
        />
        <Divider />
        {!isCustomPrints && (
          <SpecialItem
            onClick={(e) => {
              onToggleInStock(e)
              window.scrollTo({ behavior: "smooth", top: 0 })
            }}
          >
            In Stock Only
            <SwitchButton
              active={showInStock}
              ariaLabel={showInStock ? "Show In Stock Only" : "Show All"}
            />
          </SpecialItem>
        )}
        {renderFilters()}
      </StickyWrapper>
    </Container>
  )
}

export default SideNav
